/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { orderBy, uniqBy } from "lodash";
import { Parameters } from "../Context";
import { colors } from "../constants";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "commodities.default";

export function useHighchartGrowthTransformer(olapData: OLAPTransformerResult | null, parameters: Parameters | null | undefined): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");
  const [unitsDictItems] = useDictionary(dictionaryService, "slides-units-slide33", undefined, {
    commodity_id: parameters ? parameters.commodities : null,
  });
  
  const olapColumns = useMemo(() => {
    const allColumns = [olapData].filter(Boolean).flatMap(e => e?.columns);
    const uniqColumns = uniqBy(allColumns, e => e?.selection[CATEGORIES_AXIS]).filter(Boolean);
    const primarySortedColumns = orderBy(uniqColumns, e => e?.selection[CATEGORIES_AXIS]);
    const secondarySortedColumns = orderBy(primarySortedColumns, e => String(e?.selection[CATEGORIES_AXIS]).slice(1, 5));
    return secondarySortedColumns as Column[];
  }, [olapData]);

  const options = useChartOptions({
    tooltip: {
      useHTML: true,
      formatter: function () {
        const { category, serieName, unit } = this.point as any;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        return `<div>
            <b>${serieName}</b> growth for ${category}: <span>${pointValue}%</span>
        </div>`;
      },
      outside: true,
      style: {
        zIndex: 100,
      },
    },
  }, [
    chartPipe("bar"),
    legendPipe("bottom", {
      useHTML: true,
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(["12m", "24m"], {
      opposite: true,
    }),
    addYAxisPipe({
      min: undefined,
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);

          return label + "%";
        },
      },
      plotLines: [{
        color: '#525252',
        width: 2,
        value: 0
      }],
    }),
  ]);

  useEffect(() => {
    if (olapData && olapData.rows[0]) {
      const lastPoint = olapData.rows[0].points[olapData.rows[0].points.length - 1];
      
      let commodityName = olapData.rows[0].attributes[`${SERIES_AXIS}.name`];
      let unitName = "";
      if (parameters && unitsDictItems) {
        const unitDictItem = unitsDictItems.find(item => String(item.id) === String(parameters.unit));
        unitName = unitDictItem ? (unitDictItem.getValue("name") as any)[9] as string : "";
      }
      if (typeof commodityName === 'object') {
        commodityName = commodityName[9] || [];
      }
      const serieName = `${commodityName}`;

      const series: SeriesOptionsType[] = [lastPoint.last_year_value, lastPoint.two_years_ago_value].map((value, index) => {
        
        return {
          name: `${commodityName} ${unitName}`,
          //type: "column",
          data:  [{ x: index, y: value, serieName, unit: unitName }],
          yAxis: 0,
          xAxis: 0,
          color: value >= 0 ? colors[0] : colors[2],
          zIndex: 2,
          showInLegend: false,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          dataLabels: {
            enabled: false,
          }
        } as any
      });

      const result: Highcharts.Options = {
        ...options,
        series,
        plotOptions: {
          series: {
              stacking: 'normal'
          }
        },
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, olapColumns, calendarItems, unitsDictItems]);
  return state;
}
